<template>
  <v-row
    no-gutters
    class="flex-nowrap"
    :class="{
      'tw:bg-primarybackground tw:rounded-base': $attrs.isActive && measurementsGeometriesObject?.features?.length,
    }"
  >
    <v-col>
      <the-navbar-top-panel-menu
        attach="#measurementToolButton"
        :has-default-activator="false"
        v-bind="{ ...$attrs, tools: measurementTools }"
        :path="$attrs.name"
        :additional-menu-props="simple ? { absolute: true, right: true, positionX: 40, positionY: 1 } : undefined"
      >
        <template #[`menu.activator`]="{ on: onMenu }">
          <span id="measurementToolButton">
            <base-button
              v-if="simple"
              type="map-control"
              v-on="{ ...onMenu, click: measurementsClick, ...(!$attrs.isActive && { ...onMenu }) }"
              :tooltip="$attrs.tooltip"
              :icon="$attrs.isActive ? 'mdi-cancel' : $attrs.icon"
            />
            <base-button
              v-else-if="measurementsGeometriesObject?.features?.length"
              icon="mdi-cancel"
              type="text"
              color="primary"
              size="sm"
              :tooltip="$attrs.tooltip"
              @click="measurementsClick"
              :disabled="$attrs.disabled"
            />
            <table-button
              v-else
              v-on="{ ...(!$attrs.isActive && { ...onMenu }) }"
              :is-active="$attrs.isActive"
              name="saveMeasurement"
              :icon="$attrs.isActive ? 'mdi-cancel' : $attrs.icon"
              fontSize="19px"
              buttonSize="small"
              :tooltip="$attrs.tooltip"
              @click="measurementsClick"
              :disabled="$attrs.disabled"
            />
          </span>
        </template>
      </the-navbar-top-panel-menu>
    </v-col>
    <v-col v-if="!simple && measurementsGeometriesObject?.features?.length">
      <the-navbar-top-panel-menu
        ref="panel_menu"
        attach="#measurementExportButton"
        :has-default-activator="false"
        @exportToGeoJSON="exportMeasurement('geojson')"
        @exportToKML="exportMeasurement('kml')"
        @exportToSystem="exportMeasurement('system')"
        @exportFullGeometry="geometryExportMode = 'full'"
        @exportCentroids="geometryExportMode = 'centroid'"
        @closeMenu="geometryExportMode = null"
        :close-on-content-click="false"
        v-bind="{ ...$attrs, tools: measurementExportTools }"
        :path="$attrs.name"
      >
        <template #[`menu.activator`]="{ on: onMenu }">
          <span id="measurementExportButton">
            <table-button
              v-on="{ ...onMenu, ...$attrs.on }"
              name="saveMeasurement"
              icon="mdi-content-save"
              fontSize="19px"
              buttonSize="small"
              :tooltip="$i18n.t('navbar.saveMeasurement')"
            />
          </span>
        </template>
      </the-navbar-top-panel-menu>
    </v-col>
    <v-col
      v-if="
        !simple &&
        measurementsGeometriesObject?.features?.length > 1 &&
        !measurementsGeometriesObject?.features?.[0]?.geometry.type.includes('LineString')
      "
    >
      <table-button
        name="intersect"
        icon="$intersect"
        fontSize="19px"
        buttonSize="small"
        :tooltip="
          intersectActive ? $i18n.t('navbar.measurementIntersectOff') : $i18n.t('navbar.measurementIntersectOn')
        "
        :is-active="intersectActive"
        @click="intersect"
      />
    </v-col>
    <copy-features-dialog
      v-if="isCopyObjectsGeometryLoaded"
      name="measurements"
      :is-visible.sync="isCopyObjectsGeometryVisible"
      :is-loading.sync="isCopyObjectGeometryLoading"
      input-layer-id="measurements"
      :geojson="currentMeasurementCopyObject"
      :geojson-copy-attributes="mappingAttributes"
      :disable-attributes-mapping="activeTool !== 'measurementMeasures'"
    />
  </v-row>
</template>

<script>
import { get, sync } from 'vuex-pathify';

import TableButton from '@/components/TableButton';
import TheNavbarTopPanelMenu from '@/components/TheNavbarTopPanelMenu';
import turfIntersect from '@turf/intersect';
import turfUnion from '@turf/union';
import { GeoJSON } from 'ol/format';
import { getGeodesicArea, getGeometryCentroid } from '@/assets/js/mapUtils';
import { getContext } from '@/assets/js/mapContext';

export default {
  name: 'TheNavbarToolsMeasurements',
  inject: {
    mapContext: {
      default: null,
    },
  },
  components: {
    TableButton,
    TheNavbarTopPanelMenu,
    CopyFeaturesDialog: () => import('@/components/CopyFeaturesDialog'),
  },
  props: {
    simple: {
      type: Boolean,
      default: false,
    },
  },
  data: vm => ({
    geometryExportMode: null,
    currentMeasurementCopyObject: null,
    isCopyObjectsGeometryLoaded: false,
    isCopyObjectsGeometryVisible: false,
    isCopyObjectGeometryLoading: false,
    intersectActive: false,
    mappingAttributes: [
      {
        attribute: vm.$i18n.t('navbar.measurementMeasures.typeAttribute'),
        dataType: { name: 'text' },
        label: vm.$i18n.t('navbar.measurementMeasures.typeAttribute'),
      },
      {
        attribute: vm.$i18n.t('navbar.measurementMeasures.lengthAttribute'),
        dataType: { name: 'float' },
        label: vm.$i18n.t('navbar.measurementMeasures.lengthAttribute'),
      },
    ],
  }),
  computed: {
    measurementsGeometries: get('map/measurementsGeometries'),
    measurementsGeometriesObject() {
      return JSON.parse(this.measurementsGeometries || null);
    },
    measurementsIntersect: sync('map/measurementsIntersect'),
    isMeasurementsToolActive: get('tools/toolStatus@isMeasurementsToolActive'),
    activeTool: get('tools/activeTool'),
    isAdvancedDigitizingEnabled: get('admin/additionalModules@ADVANCED_DIGITIZING.enabled'),
    isDimensioningEnabled: get('admin/settingsValues@dimensioning_enabled'),
    measurementTools() {
      return [
        {
          name: 'measurementArea',
          hasDivider: false,
          hasCustomAction: true,
        },
        {
          name: 'measurementRectangle',
          hasDivider: false,
          hasCustomAction: true,
        },
        {
          name: 'measurementLength',
          hasDivider: false,
          hasCustomAction: true,
        },
        {
          name: 'measurementRadius',
          hasDivider: false,
          hasCustomAction: true,
        },
        ...(this.isAdvancedDigitizingEnabled && this.isDimensioningEnabled
          ? [
              {
                name: 'measurementMeasures',
                verboseName: this.$i18n.t('admin.advancedDigitizing.dimensioning'),
                hasCustomAction: true,
              },
            ]
          : []),
      ];
    },
    measurementExportTools() {
      return this.activeTool === 'measurementRadius' && !this.geometryExportMode
        ? [
            {
              name: 'exportFullGeometry',
              hasDivider: false,
              hasCustomAction: true,
            },
            {
              name: 'exportCentroids',
              hasDivider: false,
              hasCustomAction: true,
            },
          ]
        : [
            {
              name: 'exportToGeoJSON',
              hasDivider: false,
              hasCustomAction: true,
            },
            {
              name: 'exportToKML',
              hasDivider: false,
              hasCustomAction: true,
            },
            {
              name: 'exportToSystem',
              hasDivider: false,
              hasCustomAction: true,
            },
          ];
    },
  },
  methods: {
    getMeasurementCentroids() {
      const olFeatures = new GeoJSON().readFeatures(this.measurementsGeometriesObject);
      return {
        type: 'FeatureCollection',
        crs: {
          properties: {
            name: this.$_config.defaultEpsg,
          },
          type: 'name',
        },
        features: olFeatures.map(ft => ({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'Point',
            coordinates: getGeometryCentroid(ft.getGeometry()),
          },
        })),
      };
    },
    exportMeasurement(type) {
      this.exportHandler(type);
      this.$refs.panel_menu.close();
    },
    exportHandler(type) {
      const exportCentroids = this.activeTool === 'measurementRadius' && this.geometryExportMode === 'centroid';
      switch (type) {
        case 'kml':
        case 'geojson': {
          return getContext(this).$emit(
            'exportMeasurement',
            type,
            exportCentroids ? this.getMeasurementCentroids() : undefined
          );
        }
        case 'system': {
          this.currentMeasurementCopyObject = exportCentroids
            ? this.getMeasurementCentroids()
            : structuredClone(this.measurementsGeometriesObject);
          this.isCopyObjectsGeometryLoaded = true;
          this.isCopyObjectsGeometryVisible = true;
          return;
        }
      }
      this.$refs.panel_menu.close();
    },
    measurementsClick() {
      this.geometryExportMode = null;
      if (this.isMeasurementsToolActive) {
        getContext(this).$emit(`${this.activeTool}-action`, false);
      }
    },
    intersect() {
      if (this.intersectActive) {
        this.measurementsIntersect = null;
        this.intersectActive = false;
        return;
      }
      try {
        const intersect = turfIntersect(this.measurementsGeometriesObject);
        if (!intersect) {
          this.$store.set('snackbar/PUSH_ERROR!', {
            message: this.$i18n.t('errors.measurementIntersect'),
          });
          this.measurementsIntersect = null;
          return;
        }
        const featuresGeometries = this.measurementsGeometriesObject.features.map(feature => {
          return { ...feature.geometry, crs: this.measurementsGeometriesObject.crs };
        });
        const union = featuresGeometries.reduce(
          (geometryA, geometryB) => turfUnion(geometryA, geometryB),
          featuresGeometries[0]
        );
        const unionFeature = new GeoJSON().readFeature(union, {
          featureProjection: this.$_config.defaultEpsg || 'EPSG:3857',
          dataProjection: this.$_config.defaultEpsg || 'EPSG:3857',
        });
        const area = getGeodesicArea(unionFeature.getGeometry());
        intersect.properties.union_area = area;
        this.measurementsIntersect = intersect;
        this.intersectActive = true;
      } catch {
        this.measurementsIntersect = null;
      }
    },
  },
  watch: {
    measurementsIntersect: {
      handler(nV) {
        if (!nV) this.intersectActive = false;
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.measurementsClick();
  },
};
</script>
